/**
 * OpenAI Provider Implementation
 * 
 * Implements the IAIProvider interface for OpenAI's API.
 * Supports models like GPT-4o, GPT-5 Mini, GPT-5.2, etc.
 */

import {
  AIProvider,
  AIModelConfig,
  AIMessage,
  AIRequestOptions,
  AIResponse
} from '../AIProvider.types';
import { BaseAIProvider } from '../BaseAIProvider';
import { OPENAI_MODELS } from '../config/modelConfig';
import { PROVIDER_ENDPOINTS } from '../config/providerConfig';
import { ApiClient } from '../../ApiClient';

/**
 * OpenAI API response format
 */
interface OpenAIResponse {
  id: string;
  object: string;
  created: number;
  model: string;
  choices: Array<{
    index: number;
    message: {
      role: string;
      content: string;
    };
    finish_reason: string;
  }>;
  usage: {
    prompt_tokens: number;
    completion_tokens: number;
    total_tokens: number;
  };
}

/**
 * OpenAI message format
 */
interface OpenAIMessage {
  role: 'system' | 'user' | 'assistant';
  content: string;
}

/**
 * OpenAI Provider
 * 
 * Handles all communication with OpenAI's API, including:
 * - Chat completions
 * - JSON mode
 * - Token management
 */
export class OpenAIProvider extends BaseAIProvider {
  readonly provider = AIProvider.OPENAI;
  
  /**
   * Get the OpenAI API token from Forge
   */
  protected async getAuthToken(): Promise<string> {
    return await this.invokeResolver<string>('getOpenApiToken');
  }
  
  /**
   * Generate a completion using OpenAI's API
   */
  async generateCompletion(
    messages: AIMessage[],
    options: AIRequestOptions
  ): Promise<AIResponse> {
    this.validateRequestOptions(options);
    
    try {
      const token = await this.getAuthToken();
      
      const payload = this.buildRequestPayload(messages, options);
      
      const response = await ApiClient.post(
        PROVIDER_ENDPOINTS[AIProvider.OPENAI],
        payload,
        { Authorization: `Bearer ${token}` }
      ) as OpenAIResponse;
      
      return this.normalizeResponse(response);
    } catch (error) {
      this.handleError(error);
    }
  }
  
  /**
   * Build the request payload for OpenAI's API
   */
  private buildRequestPayload(
    messages: AIMessage[],
    options: AIRequestOptions
  ): Record<string, unknown> {
    const formattedMessages = this.formatMessages(messages, options.systemPrompt);
    
    const payload: Record<string, unknown> = {
      model: options.model,
      temperature: options.temperature ?? 0,
      messages: formattedMessages
    };
    
    // Add max_tokens if specified
    if (options.maxTokens) {
      payload.max_tokens = options.maxTokens;
    }
    
    // Add structured output if requested
    if (options.jsonMode) {
      payload.response_format = {
        type: 'json_schema',
        json_schema: {
          name: 'structured_output',
          strict: true,
          schema: {
            type: 'object',
            additionalProperties: true
          }
        }
      };
    }
    
    return payload;
  }
  
  /**
   * Format messages for OpenAI's API
   * OpenAI supports system messages directly in the messages array
   */
  private formatMessages(
    messages: AIMessage[],
    systemPrompt?: string
  ): OpenAIMessage[] {
    const formatted: OpenAIMessage[] = [];
    
    // Add system prompt if provided
    if (systemPrompt) {
      formatted.push({
        role: 'system',
        content: systemPrompt
      });
    }
    
    // Add conversation messages
    for (const msg of this.normalizeMessages(messages)) {
      formatted.push({
        role: msg.role,
        content: msg.content
      });
    }
    
    return formatted;
  }
  
  /**
   * Normalize OpenAI's response to our standard format
   */
  private normalizeResponse(response: OpenAIResponse): AIResponse {
    const choice = response.choices[0];
    
    return {
      content: choice?.message?.content || '',
      model: response.model,
      provider: this.provider,
      usage: {
        promptTokens: response.usage?.prompt_tokens || 0,
        completionTokens: response.usage?.completion_tokens || 0,
        totalTokens: response.usage?.total_tokens || 0
      },
      finishReason: choice?.finish_reason || 'stop'
    };
  }
  
  /**
   * Check if OpenAI provider is available
   */
  async isAvailable(): Promise<boolean> {
    try {
      const token = await this.getAuthToken();
      return !!token && token.length > 0;
    } catch {
      return false;
    }
  }
  
  /**
   * Get available OpenAI models
   */
  getAvailableModels(): AIModelConfig[] {
    return OPENAI_MODELS;
  }
}
